var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { flowRestrictionFindingSeverities, } from "api/computation/types";
/* The sense valve state during the transfer phase, i.e. while the part is held */
export var getFlowRestrictionState = function (performance) {
    if (!performance.isSenseValve)
        return "restrictor";
    return performance.senseValveOpenDuringTransfer ? "open" : "closed";
};
export var isFlowRestrictionFunctioningAsIntended = function (performance) {
    var state = getFlowRestrictionState(performance);
    if (state === "restrictor")
        return true;
    return state === (performance.isCupOutsideOfObject ? "closed" : "open");
};
var oppositeState = {
    open: "closed",
    closed: "open",
};
var summarizeSide = function (restrictions, intendedState, hasSenseValves) {
    if (restrictions.length === 0) {
        return {
            count: 0,
            functionsAsIntended: true,
            /* An empty side follows the rest of the system */
            groups: [
                {
                    state: hasSenseValves ? intendedState : "restrictor",
                    count: 0,
                    functionsAsIntended: true,
                },
            ],
        };
    }
    var groups = [intendedState, "restrictor", oppositeState[intendedState]]
        .map(function (state) {
        var inState = restrictions.filter(function (_a) {
            var performance = _a.performance;
            return getFlowRestrictionState(performance) === state;
        });
        return {
            state: state,
            count: inState.length,
            functionsAsIntended: state !== oppositeState[intendedState],
            findingSeverity: getWorstFindingSeverity(inState.flatMap(function (_a) {
                var severity = _a.findingSeverity;
                return severity ? [{ severity: severity }] : [];
            })),
        };
    })
        .filter(function (_a) {
        var count = _a.count;
        return count > 0;
    });
    return {
        count: restrictions.length,
        functionsAsIntended: groups.every(function (_a) {
            var functionsAsIntended = _a.functionsAsIntended;
            return functionsAsIntended;
        }),
        groups: groups,
    };
};
/* Undefined when no grip position has a calculated flow restriction */
export var summarizeFlowRestrictions = function (restrictions) {
    if (restrictions.length === 0)
        return;
    var hasSenseValves = restrictions.some(function (_a) {
        var performance = _a.performance;
        return performance.isSenseValve;
    });
    return {
        codes: __spreadArray([], __read(new Set(restrictions.map(function (_a) {
            var code = _a.code;
            return code;
        }))), false).sort(),
        hasSenseValves: hasSenseValves,
        onObject: summarizeSide(restrictions.filter(function (_a) {
            var performance = _a.performance;
            return !performance.isCupOutsideOfObject;
        }), "open", hasSenseValves),
        offObject: summarizeSide(restrictions.filter(function (_a) {
            var performance = _a.performance;
            return performance.isCupOutsideOfObject;
        }), "closed", hasSenseValves),
    };
};
/* The API names cups by their API id ("Cup 102 has no material"), which the
 * user never sees. The finding is shown with its cup marked in the canvas, so
 * "the cup" says enough, and it lets the same finding on several cups group. */
export var withoutCupIds = function (text) {
    return text.replace(/\b([Cc])up \d+\b/g, function (_match, c) {
        return c === "C" ? "The cup" : "the cup";
    });
};
/* Names the one cup/pump a finding is about by its letter in the results,
 * e.g. "The cup (a1) has no material" */
export var withNodeLabel = function (text, label) {
    return text.replace(/\b([Tt]he|this) (cup|pump)\b/g, "$1 $2 (".concat(label, ")"));
};
/* The API joins its recommendations with ", ", and each one starts with a
 * capital letter, unlike a clause within one ("..., or, increase the ...") */
export var splitRecommendations = function (recommendation) {
    return recommendation
        .split(/,\s+(?=[A-Z])/)
        .map(function (part) { return part.trim(); })
        .filter(function (part) { return part.length > 0; });
};
export var compareFindingSeverity = function (a, b) {
    return flowRestrictionFindingSeverities.indexOf(a) -
        flowRestrictionFindingSeverities.indexOf(b);
};
/* What outlines and pulses the box. The findings also catch what the state
 * during the transfer does not, e.g. a valve on the part that closes for a
 * moment while evacuating, but the valves stay coloured by that state. */
export var needsAttention = function (severity) {
    return severity === "critical" || severity === "warning";
};
/* Undefined without findings */
export var getWorstFindingSeverity = function (findings) {
    return findings.map(function (_a) {
        var severity = _a.severity;
        return severity;
    }).sort(compareFindingSeverity)[0];
};
/* Identical findings grouped, the worst first */
export var groupFlowRestrictionFindings = function (findings) {
    var groups = new Map();
    findings.forEach(function (_a) {
        var nodeId = _a.nodeId, isPump = _a.isPump, severity = _a.severity, title = _a.title, finding = _a.finding, recommendation = _a.recommendation;
        var text = withoutCupIds(finding);
        var key = [isPump, severity, title, text, recommendation].join("\n");
        var group = groups.get(key);
        if (group) {
            group.nodeIds.push(nodeId);
        }
        else {
            groups.set(key, {
                severity: severity,
                title: title,
                finding: text,
                recommendations: splitRecommendations(withoutCupIds(recommendation)),
                nodeIds: [nodeId],
                isPump: isPump,
            });
        }
    });
    /* Array.prototype.sort is stable, so the API's order holds within a severity */
    return __spreadArray([], __read(groups.values()), false).sort(function (a, b) {
        return compareFindingSeverity(a.severity, b.severity);
    });
};
